import React, { ReactNode, type PropsWithChildren } from 'react'
import { InputRange } from '@blockComponents/InputRange'
import { ANALITYCS_InvestorFordStartInvestingButtonIds } from '@constants/analitycs-ids'
import { BlockAnchors } from '@constants/forms'
import { Button } from '@shared/components/button'
import { Section } from '@shared/components/section'
import { Spoiler } from '@shared/components/spoiler'
import { ButtonLink } from '@shared/componentsStyled/Button'
import { Link } from '@shared/componentsStyled/Link'
import { changeDocumentHash } from '@shared/pipes/dom'
import scrollToElement from '@shared/pipes/scroll'
import { yaCounter } from '@shared/pipes/ya-counter'
import { breaksHundreds, formatCalcSum } from '@shared/utils/helpers'
import styled, { CSSObject, type DefaultTheme } from 'styled-components'

import { getAmountFromSteps, useCalc } from './data'
import { wordEnding } from '@shared/utils/wordEnding'
import { usePageSettings } from '@providers/page-settings'
import type { PageSettings } from '@http/api-models/page-settings'
import type { SavingCalculatorProgramsResponse } from '@http/api-models/SavingPrograms'

const logInvestCalc = (): void => yaCounter.utils.reachGoal('invest_calculator')
const logInvestLead = (): void => yaCounter.utils.reachGoal('invest_lead')
const logInvestTg = (): void => yaCounter.utils.reachGoal('invest_business_tg')

type BlockBackgroundColor = 'lightmint' | 'pacificblue10'

interface ProgramProps {
	programs: Array<{
		title: ReactNode
		list: Array<{
			title: string
			termMonths: number
			paymentType: string
			ndfl: number
			annualProfitPercentage: number
			annualProfitAfterTaxesAmount: number
			backgroundColor?: BlockBackgroundColor
		}>
	}>
}

const Programs = ({ programs }: ProgramProps) => {
	return (
		<Grid.Section>
			{programs.map(
				(program, idx) =>
					program.list.length > 0 && (
						<Grid.Section key={idx} $gap='xs'>
							<ProgramTitle>{program.title}</ProgramTitle>
							<Grid.Row
								$gap='s'
								styleOverride={(theme) => ({
									[theme.mediaQueries.mediaMinWidthMobile]: {
										gap: theme.spacingsDesktop.xl,
										rowGap: theme.spacingsDesktop.m,
									},
								})}
							>
								{program.list.map(
									({
										title,
										backgroundColor,
										termMonths,
										paymentType,
										annualProfitPercentage,
										ndfl,
										annualProfitAfterTaxesAmount,
									}) => (
										<Block key={title} backgroundColor={backgroundColor}>
											<Grid.Section $gap='s'>
												<Grid.Section $gap={0}>
													<Grid.Row
														$sizes='1fr auto'
														$gap='s'
														styleOverride={({ typography, mediaQueries }) => ({
															...typography.text18_24,
															[mediaQueries.mediaMinWidthMobile]: {
																...typography.text24_32,
															},
															alignItems: 'center',
															fontWeight: 'bold !important',
														})}
													>
														<div>{title}</div>
														<span>
															{termMonths}{' '}
															{wordEnding(termMonths, { 1: 'месяц', 2: 'месяца', 5: 'месяцев' })}
														</span>
													</Grid.Row>
													<Grid.Row
														styleOverride={(theme) => ({
															...theme.typography.text18_24,
															color: theme.colors.black50,
														})}
													>
														Выплата {paymentType.toLowerCase()}
													</Grid.Row>
												</Grid.Section>
												<Grid.Row $sizes='1.25fr .5fr 1fr' $gap='s'>
													<Grid.Section $gap={0}>
														<Grid.Section
															styleOverride={(theme) => ({
																...theme.typography.text18_24,
																color: theme.colors.black50,
															})}
														>
															Ставка
														</Grid.Section>
														<Grid.Section
															styleOverride={(theme) => ({
																...theme.typography.text18_24,
																[theme.mediaQueries.mediaMinWidthMobile]: {
																	...theme.typography.text24_32,
																},
															})}
														>
															{annualProfitPercentage}% годовых
														</Grid.Section>
													</Grid.Section>
													<Grid.Section $gap={0}>
														<Grid.Section
															styleOverride={(theme) => ({
																...theme.typography.text18_24,
																color: theme.colors.black50,
															})}
														>
															НДФЛ
														</Grid.Section>
														<Grid.Section
															styleOverride={(theme) => ({
																color: theme.colors.main,
																...theme.typography.text18_24,
																[theme.mediaQueries.mediaMinWidthMobile]: {
																	...theme.typography.text24_32,
																},
															})}
														>
															<Link
																href='https://percent.credit.club/nalogooblozhenie-sberezhenij/'
																target='_blank'
																rel='noopener noreferrer nofollow'
															>
																{ndfl}%
															</Link>
														</Grid.Section>
													</Grid.Section>
													<Grid.Section $gap={0}>
														<Grid.Section
															styleOverride={(theme) => ({
																...theme.typography.text18_24,
																color: theme.colors.black50,
															})}
														>
															Доход
														</Grid.Section>
														<Grid.Section
															styleOverride={(theme) => ({
																...theme.typography.text18_24,
																[theme.mediaQueries.mediaMinWidthMobile]: {
																	...theme.typography.text24_32,
																},
															})}
														>
															{breaksHundreds(annualProfitAfterTaxesAmount) + ' ₽'}
														</Grid.Section>
													</Grid.Section>
												</Grid.Row>
											</Grid.Section>
										</Block>
									)
								)}
							</Grid.Row>
						</Grid.Section>
					)
			)}
		</Grid.Section>
	)
}

export interface OwnProps {
	formLink?: string
	warn?: ReactNode
	annualInterestRate?: [string, string]
	onButtonClick?: () => void
}

export const goToCalculatorLink = () => {
	scrollToElement(`#${BlockAnchors.calculator}`, { time: 0 }, () => {
		changeDocumentHash(BlockAnchors.calculator)
	})
}

export function BlockInvestCalculator({
	formLink,
	annualInterestRate = ['', ''],
	warn,
	onButtonClick,
}: OwnProps): JSX.Element {
	const { savings } = usePageSettings() as PageSettings & {
		savings: { programs: SavingCalculatorProgramsResponse }
	}

	const {
		rangeValue,
		handleChange,
		programs: [programsForNewClient = [], programsForAllClients = []],
		min,
		max,
	} = useCalc({ initialPrograms: savings?.programs ?? [] })

	return (
		<>
			<Section id={BlockAnchors.calculator}>
				<ViewRoot>
					<TextTitle>Рассчитайте доходность</TextTitle>
					<TextSub noMargin={!!warn}>
						Для нового клиента повышенная доходность на сумму до 1 млн рублей включительно.
					</TextSub>
					{warn && <Warn>{warn}</Warn>}
					<Grid.Section $gap='s'>
						<Grid.Row
							$gap='s'
							styleOverride={(theme) => ({
								[theme.mediaQueries.mediaMinWidthMobile]: { gap: theme.spacingsDesktop.xl },
							})}
						>
							<InputRow>
								<TextLabel htmlFor='investFondAmount'>Сумма сбережений, рублей</TextLabel>
								<InputRange
									value={rangeValue}
									onChange={handleChange}
									step={1}
									max={max}
									min={min}
									formatLabel={(rangeValue) =>
										`${formatCalcSum(getAmountFromSteps(+rangeValue))} рублей`
									}
									labelClassName='calcLabel'
									activeTrackClassName='calcTrack'
									inputRangeClassName='calcRange'
									resultClassName='calcResult'
								/>
							</InputRow>
							<Grid.Row
								$gap={17}
								$sizes='280px 1fr'
								styleOverride={(theme) => ({
									alignItems: 'center',
									[theme.mediaQueries.mediaMinWidthMobile]: { marginTop: 34 },
								})}
							>
								{onButtonClick ? (
									<FormButton
										type='button'
										onClick={() => {
											logInvestLead()
											onButtonClick()
										}}
									>
										Открыть сбережение
									</FormButton>
								) : (
									<ButtonInvest
										target='_blank'
										rel='noopener noreferrer nofollow'
										href={formLink}
										id={ANALITYCS_InvestorFordStartInvestingButtonIds(3)}
										onClick={logInvestCalc}
									>
										Вложить
									</ButtonInvest>
								)}
								<BiggerTextNote>
									или{' '}
									<Link
										style={{ cursor: 'pointer' }}
										target='_blank'
										rel='noopener noreferrer nofollow'
										href='https://t.me/invest_creditclub/1900'
									>
										узнать подробнее в чате Телеграм
									</Link>
								</BiggerTextNote>
							</Grid.Row>
						</Grid.Row>
						<Programs
							programs={[
								{
									title: 'Условия сбережений доступные только новому клиенту',
									list: programsForNewClient.map((program) => ({
										...program,
										backgroundColor: 'lightmint',
									})),
								},
								{
									title: 'Условия сбережений доступные всем клиентам',
									list: programsForAllClients.map((program) => ({
										...program,
										backgroundColor: 'pacificblue10',
									})),
								},
							]}
						/>
						<Grid.Row
							styleOverride={({ typography, colors }) => ({
								...typography.text12_16,
								color: colors.black50,
							})}
						>
							Расчет является предварительным. Не является публичной офертой.
						</Grid.Row>
					</Grid.Section>
				</ViewRoot>
			</Section>
			<Section>
				<ViewSpoiler>
					<Spoiler buttonText='Подробные условия' buttonTextAlign='right'>
						<noindex>
							Услуги физическим лицам оказывает Кредитный потребительский кооператив «Фонд
							Ипотечного Кредитования» (Свердловская область, г. Екатеринбург, ул. Малышева, стр.
							51, оф. 26/14. Бизнес-центр «Высоцкий» ОГРН 1196658040677 ИНН 6678102434, официальный
							сайт https://loanfund.ru/) в рамках Федерального закона No 190-ФЗ от 18.07.2009 года
							путем заключения договора передачи личных сбережений.
							<br />
							<br />
							Условия договора передачи личных сбережений:
							<br /> — Сумма передаваемых личных сбережений от 10 000 до 30 000 000 рублей.
							<br /> — Ставка процентов за пользование кооперативом привлечёнными денежными
							средствами члена Кооператива — от {annualInterestRate[0]}% до {annualInterestRate[1]}%
							годовых, определяется индивидуально исходя из выбранной категории программы
							привлечения денежных средств членов Кооператива и Ключевой ставки Банка России. В
							случае досрочного расторжения договора по инициативе клиента возможен пересчет
							процентов по ставке 0.1% годовых.
							<br /> — Срок договора личных сбережений: от 3 до 60 месяцев. Выплата процентов
							осуществляется ежемесячно или при возврате суммы сбережений.
							<br />
							<br />
							Кредитный потребительский кооператив оказывает услуги исключительно членам кредитного
							кооператива (пайщикам). При вступлении в кооператив вносится обязательный паевой взнос
							в размере 50 рублей. Члены кредитного кооператива (пайщики) обязаны нести субсидиарную
							ответственность по обязательствам кооператива в пределах невнесенной части
							дополнительного взноса каждого из членов кредитного кооператива (пайщиков).
							<br />
							<br />
							Размещение сбережений осуществляется через участника проекта инновационного центра
							«Сколково» ООО «Кредит.Клаб» (Свердловская область, г. Екатеринбург, ул. Малышева,
							стр. 51, оф. 26/14. Бизнес-центр «Высоцкий» ОГРН 1196658084743 ИНН 6678105594
							официальный сайт https://credit.club/).
							<br />
							<br />
							Продукт «Сбережения» не относится к инвестициям, предусмотренным федеральным законом
							от 02.08.2019 года № 259-ФЗ «О привлечении инвестиций с использованием инвестиционных
							платформ и о внесении изменений в отдельные законодательные акты Российской
							Федерации». Не является публичной офертой.
						</noindex>
					</Spoiler>
				</ViewSpoiler>
			</Section>
		</>
	)
}

const ViewRoot = styled.div(({ theme }) => ({
	border: '1px solid rgba(216, 234, 228, 0.25)',
	// borderTopLeftRadius: 40,
	// borderTopRightRadius: 40,
	borderRadius: 40,
	marginBottom: 20,
	boxShadow: '0px 10px 30px rgba(163, 198, 186, 0.25)',
	paddingTop: 20,
	paddingLeft: 20,
	paddingRight: 20,
	paddingBottom: 20,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		paddingTop: 40,
		paddingLeft: 60,
		paddingBottom: 40,
		paddingRight: 60,
	},
	'& .calcLabel': {
		...theme.typography.text12_16,
	},
	'& .calcTrack': {
		backgroundColor: theme.colors.lightbrown,
	},
	'& .calcRange': {
		backgroundColor: theme.colors.peach,
	},
	'& .calcResult': {
		...theme.typography.text18_24,
		backgroundColor: theme.colors.lightmint,
		height: 40,
		paddingBottom: 4,
		[theme.mediaQueries.mediaMinWidthMobile]: {
			...theme.typography.text24_32,
			height: 64,
			paddingBottom: 0,
		},
	},
}))

const Grid = {
	Section: styled.div<
		PropsWithChildren<{
			styleOverride?: CSSObject | ((theme: DefaultTheme) => CSSObject)
			$gap?: keyof DefaultTheme['spacingsMobile'] | number
		}>
	>(({ theme, $gap = 'm', styleOverride = {} }) => ({
		display: 'grid',
		position: 'relative',
		gridTemplateColumns: '1fr',
		gap: typeof $gap === 'number' ? $gap : theme.spacingsMobile[$gap],

		[theme.mediaQueries.mediaMinWidthTablet]: {
			gap: typeof $gap === 'number' ? $gap : theme.spacingsDesktop[$gap],
		},

		...(typeof styleOverride === 'function' ? styleOverride(theme) : styleOverride),
	})),

	Row: styled.div<
		PropsWithChildren<{
			styleOverride?: CSSObject | ((theme: DefaultTheme) => CSSObject)
			$sizes?: string
			$gap?: keyof DefaultTheme['spacingsMobile'] | number
		}>
	>(({ theme, styleOverride = {}, $gap = 'm', $sizes = '1fr 1fr' }) => ({
		display: 'grid',
		gap: typeof $gap === 'number' ? $gap : theme.spacingsMobile[$gap],
		overflowWrap: 'break-word',
		height: 'min-content',
		gridTemplateColumns: '1fr',
		gridAutoColumns: 'min-content',

		[theme.mediaQueries.mediaMinWidthTablet]: {
			gridTemplateColumns: $sizes,
			gap: typeof $gap === 'number' ? $gap : theme.spacingsDesktop[$gap],
		},

		...(typeof styleOverride === 'function' ? styleOverride(theme) : styleOverride),
	})),
}

const Disclaimer = styled.div(({ theme }) => ({
	borderBottomLeftRadius: 40,
	borderBottomRightRadius: 40,
	paddingTop: 16,
	paddingLeft: 20,
	paddingRight: 20,
	paddingBottom: 16,
	marginBottom: 15,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		paddingLeft: 60,
		paddingRight: 60,
	},
	backgroundColor: theme.colors.lightmint,
}))

const ViewOpenDeposit = styled.div(({ theme }) => ({
	display: 'flex',
	flexDirection: 'row',
	gap: 16,
}))

const DisclaimerText = styled.h3(({ theme }) => ({
	...theme.typography.text24_32,
}))

const TextTitle = styled.h2(({ theme }) => ({
	...theme.typography.header30_36,
	marginBottom: 20,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.header56_60,
		marginBottom: 20,
	},
}))

const TextSub = styled.div<{ noMargin?: boolean }>(({ theme, noMargin }) => ({
	...theme.typography.text18_24,
	marginBottom: noMargin ? 10 : 20,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text24_32,
		marginBottom: noMargin ? 10 : 40,
	},
}))

const ProgramTitle = styled.div(({ theme }) => ({
	...theme.typography.text18_24,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text24_32,
	},
}))

const Warn = styled.div(({ theme }) => ({
	...theme.typography.text18_24,
	backgroundColor: '#fff6b3',
	padding: 8,
	borderRadius: 8,
	marginBottom: 20,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text24_32,
		marginBottom: 20,
	},
}))

const InputRow = styled.div(() => ({
	marginBottom: 10,
}))

const Block = styled.div<{ backgroundColor?: BlockBackgroundColor }>(
	({ theme, backgroundColor = 'lightmint05' }) =>
		({
			flex: 1,
			width: '100%',
			backgroundColor: theme.colors[backgroundColor],
			borderRadius: 8,
			alignSelf: 'flex-end',
			height: 'min-content',
			padding: '10px 15px',
			display: 'flex',
			flexDirection: 'column',
			[theme.mediaQueries.mediaMinWidthMobile]: {
				padding: '20px 30px',
			},
		} as CSSObject)
)

const TextLabel = styled.label(({ theme }) => ({
	...theme.typography.text14_18,
	opacity: 0.5,
	display: 'block',
	marginBottom: 5,
	whiteSpace: 'normal',

	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text18_24,
		marginBottom: 10,
		whiteSpace: 'nowrap',
	},
}))

const TextLabelSmall = styled.label(({ theme }) => ({
	...theme.typography.text14_18,
	opacity: 0.5,
	display: 'block',
	marginBottom: 5,
	whiteSpace: 'normal',

	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text18_24,
		whiteSpace: 'nowrap',
	},
}))

const ViewCalcRow = styled.div(
	({ theme }) =>
		({
			display: 'flex',
			flexDirection: 'column',
			[theme.mediaQueries.mediaMinWidthMobile]: {
				flexDirection: 'row',
			},
		} as CSSObject)
)

const ViewCalcCol = styled.div(({ theme }) => ({
	flex: 1,
	marginRight: 0,
	'&:first-child': {
		marginBottom: 10,
	},
	[theme.mediaQueries.mediaMinWidthMobile]: {
		'&:first-child': {
			marginRight: 20,
			marginBottom: 0,
		},
	},
}))

const ViewResRow = styled.div(
	({ theme }) =>
		({
			display: 'flex',
			flexDirection: 'column',
			width: '100%',
			[theme.mediaQueries.mediaMinWidthMobile]: {
				display: 'grid',
				gridTemplateColumns: 'auto auto auto',
				gap: 16,
				flexDirection: 'row',
			},
		} as CSSObject)
)

const ViewResItem = styled.div(({ theme }) => ({
	flex: 1,
	marginRight: 0,
	display: 'block',
}))

const TextResItem = styled.div<{ blue?: boolean }>(({ theme, blue }) => ({
	...theme.typography.text18_24,
	color: blue ? theme.colors.main : undefined,
	marginBottom: 10,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text24_32,
		marginBottom: 28,
	},
}))

const ButtonInvest = styled(ButtonLink)(({ theme }) => ({
	marginBottom: 20,
	alignSelf: 'flex-start',
	[theme.mediaQueries.mediaMinWidthMobile]: {
		marginBottom: 20,
	},
}))

const FormButton = styled(Button)(({ theme }) => ({
	whiteSpace: 'nowrap',
	padding: 0,
}))

const TextNote = styled.p(({ theme }) => ({
	opacity: 0.5,
	...theme.typography.text12_16,
	marginBottom: 20,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		marginBottom: 0,
	},
}))

const BiggerTextNote = styled.p(({ theme }) => ({
	color: theme.colors.black50,
	...theme.typography.text14_18,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text18_24,
	},
}))

const ViewSpoiler = styled.div(({ theme }) => ({
	...theme.typography.text12_16,
	opacity: 0.5,
	marginBottom: 50,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		marginBottom: 100,
	},
}))
